import { useState, useEffect } from "react";
import { getCompanyBranches } from "../services/companyService";
import useDebounce from "./useDebounce";

interface RawBranch {
  id: string;
  branchName: string;
  address?: string;
  city?: string;
  state?: string;
}

export interface Branch {
  id: string;
  name: string;
  address: string;
}

const PAGE_SIZE = 10;

const useGetBranches = (bankId: string | undefined) => {
  const [options, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState("");
  const [loadParams, setLoadParams] = useState({ search: "", page: 1 });

  const debouncedSearch = useDebounce(search, 400);

  useEffect(() => {
    setLoadParams({ search: debouncedSearch, page: 1 });
  }, [debouncedSearch]);

  // Reset when bank changes
  useEffect(() => {
    setBranches([]);
    setHasMore(false);
    setLoadParams({ search: "", page: 1 });
    setSearch("");
  }, [bankId]);

  useEffect(() => {
    if (!bankId) return;
    let cancelled = false;

    const fetchBranches = async () => {
      setLoading(true);
      const { search: s, page } = loadParams;
      const params: Record<string, unknown> = { page, limit: PAGE_SIZE };
      if (s.trim()) params.q = s.trim();

      const { data, error } = (await getCompanyBranches(bankId, params)) as {
        data: { data?: RawBranch[]; pagination?: { total: number } } | null;
        error: unknown;
      };

      if (cancelled) return;

      if (!error) {
        const list: RawBranch[] = Array.isArray(data?.data) ? data!.data! : [];
        const normalized: Branch[] = list.map((b) => ({
          id: b.id,
          name: b.branchName,
          address: [b.address, b.city, b.state].filter(Boolean).join(", "),
        }));
        const total = data?.pagination?.total ?? 0;
        setBranches((prev) => {
          const updated = page === 1 ? normalized : [...prev, ...normalized];
          setHasMore(updated.length < total);
          return updated;
        });
      }
      setLoading(false);
    };

    fetchBranches();
    return () => {
      cancelled = true;
    };
  }, [bankId, loadParams]);

  return {
    options,
    loading,
    hasMore,
    onSearch: (value: string) => setSearch(value),
    onLoadMore: () =>
      setLoadParams((prev) => ({ ...prev, page: prev.page + 1 })),
  };
};

export default useGetBranches;
